(() => {
  // Theme editor section reloads may execute this asset more than once.
  if (customElements.get("hero-lifecycle")) return;

  class HeroLifecycle extends HTMLElement {
    connectedCallback() {
      this.hero = this.closest(".hero");
      if (!this.hero) return;
      const hero = this.hero;
      this.motion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.video = hero.querySelector("video");
      this.ticker = hero.querySelector(".hero_ticker-wrapper");
      this.toggle = hero.querySelector(".hero_motion-toggle");
      hero.dataset.tickerPaused = "false";
      this.onMotion = () => this.updateMotion();
      this.motion.addEventListener("change", this.onMotion);
      if (this.toggle) {
        this.onToggle = () => {
          const paused = hero.dataset.tickerPaused !== "true";
          hero.dataset.tickerPaused = String(paused);
          this.toggle.setAttribute("aria-pressed", String(paused));
          this.toggle.textContent = paused ? "Lauftext fortsetzen" : "Lauftext pausieren";
        };
        this.toggle.addEventListener("click", this.onToggle);
      }
      if (this.ticker) {
        this.resizeObserver = new ResizeObserver(() => this.sizeTicker());
        this.resizeObserver.observe(this.ticker.firstElementChild);
        this.resizeObserver.observe(hero);
        this.sizeTicker();
      }
      if (this.video || this.ticker) {
        this.observer = new IntersectionObserver(([entry]) => {
          hero.dataset.inView = String(entry.isIntersecting);
          this.updateMotion();
        });
        this.observer.observe(hero);
      }
      this.setupReviews();
      this.setupSlides();
      this.fitBottles();
      this.updateMotion();
    }

    setupReviews() {
      // With "Eure Bewertungen" on the page the stars lead there; the Google dialog is the fallback
      // The trust band below the hero holds a second rating link, so search the whole section.
      const ratings = (this.closest(".hero-section") || this.hero).querySelectorAll(".hero_rating");
      const reviewsSection = () => {
        const section = document.getElementById("loox-reviews");
        return section && !section.closest("[hidden]") ? section : null;
      };
      if (reviewsSection()) {
        this.reviewEvents = new AbortController();
        ratings.forEach((link) => {
          link.href = "#loox-reviews";
          link.removeAttribute("target");
          link.removeAttribute("rel");
          link.setAttribute("aria-label", "Bewertungen ansehen");
          link.addEventListener("click", (event) => {
            const section = reviewsSection();
            if (!section) return;
            event.preventDefault();
            section.scrollIntoView({ behavior: "smooth", block: "start" });
          }, { signal: this.reviewEvents.signal });
        });
        return;
      }
      const dialog = this.closest(".hero-section")?.querySelector(".hero_reviews-dialog");
      if (!dialog || typeof dialog.showModal !== "function") return;
      this.reviewsDialog = dialog;
      this.reviewEvents = new AbortController();
      const options = { signal: this.reviewEvents.signal };
      ratings.forEach((link) => {
        link.setAttribute("aria-haspopup", "dialog");
        link.setAttribute("aria-controls", dialog.id);
        link.setAttribute("aria-label", "Google-Rezensionen ansehen");
        link.addEventListener("click", (event) => {
          event.preventDefault();
          dialog.showModal();
        }, options);
      });
      dialog.querySelector("[data-reviews-cancel]").addEventListener("click", () => dialog.close(), options);
      dialog.querySelector("[data-reviews-continue]").addEventListener("click", () => dialog.close(), options);
      dialog.addEventListener("click", (event) => {
        const rect = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
      }, options);
    }

    setupSlides() {
      this.slides = [...this.hero.querySelectorAll("[data-hero-slide]")];
      this.controls = this.hero.querySelector(".hero_slide-controls");
      this.activeSlide = 0;
      if (this.slides.length < 2 || !this.controls) return;
      this.slideEvents = new AbortController();
      const options = { signal: this.slideEvents.signal };
      this.status = this.controls.querySelector("[data-hero-status]");
      this.slides.forEach((slide, index) => {
        slide.setAttribute("role", "group");
        slide.setAttribute("aria-roledescription", "Folie");
        slide.setAttribute("aria-label", `${index + 1} von ${this.slides.length}`);
      });
      this.controls.hidden = false;
      // Any slide the visitor picks ends the rotation for good.
      const pick = (index) => { this.stopAutoplay(); this.showSlide(index); };
      this.controls.querySelector("[data-hero-previous]").addEventListener("click", () => pick(this.activeSlide - 1), options);
      this.controls.querySelector("[data-hero-next]").addEventListener("click", () => pick(this.activeSlide + 1), options);
      this.controls.querySelectorAll("[data-hero-dot]").forEach((dot) => {
        dot.addEventListener("click", () => pick(Number(dot.dataset.heroDot)), options);
      });
      this.controls.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        pick(this.activeSlide + (event.key === "ArrowRight" ? 1 : -1));
      }, options);
      // Touch swipe: a mostly horizontal move of 40px or more changes the slide.
      // Passive listeners keep vertical page scrolling untouched.
      let touchStart = null;
      this.hero.addEventListener("touchstart", (event) => {
        touchStart = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { ...options, passive: true });
      this.hero.addEventListener("touchend", (event) => {
        if (!touchStart) return;
        const dx = event.changedTouches[0].clientX - touchStart.x;
        const dy = event.changedTouches[0].clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        pick(this.activeSlide + (dx < 0 ? 1 : -1));
      }, { ...options, passive: true });
      this.hero.addEventListener("shopify:block:select", (event) => {
        const index = this.slides.indexOf(event.target.closest("[data-hero-slide]"));
        if (index >= 0) pick(index);
      }, options);
      this.showSlide(0, false);
      // Let the next slide fetch its image once the page has loaded, so it can fade in.
      if (document.readyState === "complete") this.preloadSlide(1);
      else window.addEventListener("load", () => this.preloadSlide(1), { ...options, once: true });
      this.setupAutoplay(options);
    }

    preloadSlide(index) {
      this.slides[(index + this.slides.length) % this.slides.length].dataset.heroReady = "";
    }

    // The progress bar in the active dot is a CSS animation; its end advances the slide. Pausing
    // the animation pauses the rotation, so bar and timer can never drift apart.
    setupAutoplay(options) {
      const hero = this.hero;
      if (hero.dataset.autoplay !== "running") return;
      if (this.motion.matches || window.Shopify?.designMode) {
        this.stopAutoplay();
        return;
      }
      this.pauseReasons = new Set();
      this.toggleButton = this.controls.querySelector("[data-hero-autoplay-toggle]");
      if (this.toggleButton) {
        this.toggleButton.hidden = false;
        this.toggleButton.addEventListener("click", () => {
          const paused = this.toggleButton.getAttribute("aria-pressed") !== "true";
          this.toggleButton.setAttribute("aria-pressed", String(paused));
          this.toggleButton.setAttribute("aria-label", paused ? "Automatischen Wechsel fortsetzen" : "Automatischen Wechsel pausieren");
          this.setPaused("button", paused);
        }, options);
      }
      // A screen reader would announce every automatic change, so the status stays quiet until the visitor takes over.
      this.status.setAttribute("aria-live", "off");
      this.controls.addEventListener("animationend", (event) => {
        if (event.animationName === "hero-autoplay-progress" && hero.dataset.autoplay === "running") {
          this.showSlide(this.activeSlide + 1);
        }
      }, options);
      hero.addEventListener("pointerenter", (event) => { if (event.pointerType === "mouse") this.setPaused("hover", true); }, options);
      hero.addEventListener("pointerleave", (event) => { if (event.pointerType === "mouse") this.setPaused("hover", false); }, options);
      // Keyboard focus only: a tapped button keeps focus, which would block its own resume.
      hero.addEventListener("focusin", (event) => { if (event.target.matches(":focus-visible")) this.setPaused("focus", true); }, options);
      hero.addEventListener("focusout", (event) => { if (!hero.contains(event.relatedTarget)) this.setPaused("focus", false); }, options);
      document.addEventListener("visibilitychange", () => this.setPaused("tab", document.hidden), options);
      this.autoplayObserver = new IntersectionObserver(([entry]) => this.setPaused("offscreen", !entry.isIntersecting));
      this.autoplayObserver.observe(hero);
      this.motion.addEventListener("change", () => { if (this.motion.matches) this.stopAutoplay(); }, options);
    }

    setPaused(reason, paused) {
      if (!this.pauseReasons) return;
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);
      this.hero.dataset.autoplayPaused = String(this.pauseReasons.size > 0);
    }

    stopAutoplay() {
      if (!this.hero.dataset.autoplay) return;
      this.hero.dataset.autoplay = "stopped";
      this.status?.setAttribute("aria-live", "polite");
      if (this.toggleButton) this.toggleButton.hidden = true;
      this.autoplayObserver?.disconnect();
    }

    showSlide(index, animate = true) {
      this.video?.pause();
      const next = (index + this.slides.length) % this.slides.length;
      const previous = this.activeSlide;
      this.activeSlide = next;
      clearTimeout(this.transitionTimer);
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== next;
        delete slide.dataset.heroEntering;
        delete slide.dataset.heroLeaving;
      });
      // The new slide fades in above the old one, which stays visible underneath until it is covered.
      if (animate && previous !== next && !this.motion.matches) {
        this.hero.dataset.heroSwitched = "";
        this.slides[previous].dataset.heroLeaving = "";
        this.slides[next].dataset.heroEntering = "";
        this.transitionTimer = setTimeout(() => {
          delete this.slides[previous].dataset.heroLeaving;
          delete this.slides[next].dataset.heroEntering;
        }, 900);
      }
      this.preloadSlide(next);
      // The first slide waits for the page load instead, so the neighbour cannot compete with it.
      if (animate) this.preloadSlide(next + 1);
      this.status.textContent = `${next + 1} / ${this.slides.length}`;
      this.controls.querySelectorAll("[data-hero-dot]").forEach((dot, i) => {
        if (i === next) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
      // The dots sit on the image and take the active slide's text colour.
      this.hero.dataset.activeTone = this.slides[next].dataset.tone || "";
      this.video = this.slides[next].querySelector("video");
      this.updateMotion();
    }

    // Packshots carry different white margins, so a plain crop shows some bottles
    // smaller than others. Find the bottle's bounding box and scale it to 85% of
    // the 4:5 tile height (narrower if a wide bottle would overflow).
    fitBottles() {
      this.hero.querySelectorAll("img[data-fit-bottle]").forEach((img) => {
        const fit = () => {
          const box = this.bottleBox(img);
          if (!box) return;
          // Units: tile height = 1, tile width = 0.8. imageWidth is the rendered image width.
          const tileWidth = 0.8;
          const ratio = img.naturalHeight / img.naturalWidth;
          const imageWidth = Math.min(0.85 / (box.height * ratio), (0.85 * tileWidth) / box.width);
          img.style.setProperty("--fit-width", `${(imageWidth / tileWidth) * 100}%`);
          img.style.setProperty("--fit-left", `${((tileWidth / 2 - (box.x + box.width / 2) * imageWidth) / tileWidth) * 100}%`);
          img.style.setProperty("--fit-top", `${(0.5 - (box.y + box.height / 2) * imageWidth * ratio) * 100}%`);
          img.dataset.fitted = "";
        };
        if (img.complete && img.naturalWidth) fit();
        else img.addEventListener("load", fit, { once: true });
      });
    }

    // Returns the non-white area as fractions of the image width and height.
    bottleBox(img) {
      const size = 120;
      const width = size;
      const height = Math.round((size * img.naturalHeight) / img.naturalWidth);
      if (!height) return null;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      context.fillStyle = "#fff";
      context.fillRect(0, 0, width, height);
      context.drawImage(img, 0, 0, width, height);
      let data;
      try {
        data = context.getImageData(0, 0, width, height).data;
      } catch {
        return null; // Cross-origin image without CORS: keep the CSS crop.
      }
      let left = width, right = -1, top = height, bottom = -1;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          if (Math.min(data[i], data[i + 1], data[i + 2]) > 232) continue;
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
      if (right < 0) return null;
      return { x: left / width, y: top / height, width: (right - left + 1) / width, height: (bottom - top + 1) / height };
    }

    sizeTicker() {
      const original = this.ticker.firstElementChild;
      const width = original.getBoundingClientRect().width;
      if (!width) return;
      this.ticker.querySelectorAll('[aria-hidden="true"]').forEach((clone) => clone.remove());
      const count = Math.min(40, Math.ceil(this.hero.clientWidth / width) + 1);
      for (let i = 0; i < count; i++) {
        const clone = original.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        this.ticker.appendChild(clone);
      }
      this.hero.style.setProperty("--hero-ticker-distance", `${width}px`);
      this.hero.dataset.tickerReady = "true";
      this.toggle.hidden = false;
    }

    updateMotion() {
      if (!this.video) return;
      // Keep native controls available, including a pause control for autoplay.
      if (this.motion.matches || this.hero.dataset.inView !== "true") {
        this.video.pause();
      } else {
        this.video.play().catch(() => {});
      }
    }

    disconnectedCallback() {
      this.reviewsDialog?.close();
      this.reviewEvents?.abort();
      this.slideEvents?.abort();
      this.autoplayObserver?.disconnect();
      clearTimeout(this.transitionTimer);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.motion?.removeEventListener("change", this.onMotion);
      this.toggle?.removeEventListener("click", this.onToggle);
      this.video?.pause();
    }
  }
  customElements.define("hero-lifecycle", HeroLifecycle);
})();
